import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Button from "components/button";
import Pagination from "components/Pagination";
import { useToast } from "components/Toast";
import {
  adminGetBusinesses,
  adminUpdateBusinessStatus,
  deleteBusiness,
  getApprovedBusinessesCount,
} from "services/businessService";
import { getStatusBadgeColor, formatStatusLabel } from "utils/businessUtils";
import { Business } from "types";

const AdminBusinessesIndex: React.FC = () => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [approvedCount, setApprovedCount] = useState(0);
  const perPage = 10;
  const [selected, setSelected] = useState<number[]>([]);
  const [sortBy, setSortBy] = useState<string>("id");
  const [sortDir, setSortDir] = useState<"ascending" | "descending">(
    "ascending"
  );

  const toast = useToast();

  const load = async (p = page, q = search) => {
    setLoading(true);
    setError(null);
    try {
      const sortDirParam = sortDir === "ascending" ? "asc" : "desc";
      const response = await adminGetBusinesses({
        search: q || undefined,
        page: p,
        per_page: perPage,
        sort_by: sortBy,
        sort_dir: sortDirParam,
      });
      if (response && response.data) {
        setBusinesses(Array.isArray(response.data) ? response.data : []);
        setTotal(response.meta?.total ?? 0);
      } else {
        setBusinesses([]);
        setTotal(0);
      }
    } catch (e: any) {
      console.error("Error loading businesses:", e);
      const status = e?.response?.status;
      const message = e?.response?.data?.message || e.message;

      let errorMsg = "Failed to load businesses";
      if (status === 401) {
        errorMsg = "Unauthorized. Please log in again.";
      } else if (status === 403) {
        errorMsg = "You don't have permission to access this resource.";
      } else if (message) {
        errorMsg = `Failed to load businesses: ${message}`;
      }

      setError(errorMsg);
      toast.showError(errorMsg);

      setBusinesses([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1, "");
    // Load approved count
    const loadApprovedCount = async () => {
      try {
        const count = await getApprovedBusinessesCount();
        console.log("Approved businesses count:", count);
        setApprovedCount(count);
      } catch (err) {
        console.error("Error loading approved count:", err);
        setApprovedCount(0);
      }
    };
    loadApprovedCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(page, search), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  useEffect(() => {
    // reload when sort changes
    load(1, search);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortDir]);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete business?")) return;
    try {
      await deleteBusiness(id);
      await load(page, search);
      toast.showSuccess("Business deleted");
    } catch (e) {
      console.error(e);
      toast.showError("Failed to delete business");
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await adminUpdateBusinessStatus(id, "approved");
      setBusinesses((prev) =>
        prev.map((b) => (b.id === id ? { ...b, status: "approved" } : b))
      );
      toast.showSuccess("Business approved");
    } catch (err: any) {
      console.error("Error approving business:", err);
      const status = err?.response?.status;
      if (status === 401) {
        toast.showError("Unauthorized. Please log in again.");
      } else if (status === 403) {
        toast.showError("You don't have permission to approve businesses.");
      } else {
        toast.showError("Failed to approve business");
      }
    }
  };

  const handleReject = async (id: number) => {
    try {
      await adminUpdateBusinessStatus(id, "rejected");
      setBusinesses((prev) =>
        prev.map((b) => (b.id === id ? { ...b, status: "rejected" } : b))
      );
      toast.showSuccess("Business rejected");
    } catch (err: any) {
      console.error("Error rejecting business:", err);
      const status = err?.response?.status;
      if (status === 401) {
        toast.showError("Unauthorized. Please log in again.");
      } else if (status === 403) {
        toast.showError("You don't have permission to reject businesses.");
      } else {
        toast.showError("Failed to reject business");
      }
    }
  };

  const toggleSort = (col: string) => {
    if (sortBy === col)
      setSortDir((d) => (d === "ascending" ? "descending" : "ascending"));
    else {
      setSortBy(col);
      setSortDir("ascending");
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-semibold">Businesses</h2>
        <div className="mt-3 sm:mt-0">
          <Button as="link" to="/admin/businesses/create">
            Create Business
          </Button>
        </div>
      </div>

      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          placeholder="Search businesses..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="input w-full sm:w-64"
        />
        <div className="text-sm text-gray-600">
          {loading ? "Loading..." : `${approvedCount} approved businesses`}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded bg-white shadow dark:bg-navy-700 dark:shadow-none">
        <table className="min-w-full">
          <thead>
            <tr className="border-b bg-gray-50 dark:bg-gray-900/50">
              <th className="p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                ID
              </th>
              <th className="p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Name
              </th>
              <th className="p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Owner
              </th>
              <th className="p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Category
              </th>
              <th className="p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Status
              </th>
              <th className="p-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {businesses.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="p-4 text-center text-gray-500 dark:text-gray-400"
                >
                  No businesses found
                </td>
              </tr>
            ) : (
              businesses.map((b) => (
                <tr
                  key={b.id}
                  className="border-t hover:bg-gray-50 dark:hover:bg-gray-900/30"
                >
                  <td className="p-3 text-sm text-gray-600 dark:text-gray-400">
                    {b.id}
                  </td>
                  <td className="p-3 text-sm font-medium text-gray-800 dark:text-white">
                    {b.name}
                  </td>
                  <td className="p-3 text-sm text-gray-600 dark:text-gray-400">
                    {b.user.firstname} {b.user.lastname}
                  </td>
                  <td className="p-3 text-sm text-gray-600 dark:text-gray-400">
                    {b.category}
                  </td>
                  <td className="p-3">
                    <span
                      className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${getStatusBadgeColor(
                        b.status
                      )}`}
                    >
                      {formatStatusLabel(b.status)}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap items-center gap-1">
                      <Link to={`/admin/businesses/${b.id}`}>
                        <Button size="sm" variant="outline">
                          View
                        </Button>
                      </Link>
                      {b.status !== "approved" && (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => handleApprove(b.id)}
                        >
                          Approve
                        </Button>
                      )}
                      {b.status !== "rejected" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(b.id)}
                        >
                          Reject
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDelete(b.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        perPage={perPage}
        total={total}
        onPageChange={setPage}
      />
    </div>
  );
};

export default AdminBusinessesIndex;
